export type FavoriteType = 'radio' | 'programa' | 'locutor' | 'evento' | 'concurso' | 'club';

export interface FavoriteTypeConfig {
  key: FavoriteType;
  label: string;
  labelPlural: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
  routePrefix: string;
}

export interface FavoriteRadio {
  id: string;
  favoriteType: 'radio';
  name: string;
  slug: string;
  image: string;
  genre: string;
  location: string;
  followers: number;
  isLive: boolean;
  currentShow: string | null;
  favoritedAt: string;
  favoritedAtRelative: string;
  frequency: string;
  score: number;
}

export interface FavoritePrograma {
  id: string;
  favoriteType: 'programa';
  name: string;
  slug: string;
  image: string;
  station: string;
  host: string;
  schedule: string;
  genre: string;
  days: string[];
  onAir: boolean;
  nextEpisode: string;
  favoritedAt: string;
  favoritedAtRelative: string;
  score: number;
}

export interface FavoriteLocutor {
  id: string;
  favoriteType: 'locutor';
  name: string;
  slug: string;
  photo: string;
  role: string;
  station: string;
  shows: string[];
  genre: string;
  followers: number;
  onAir: boolean;
  favoritedAt: string;
  favoritedAtRelative: string;
  score: number;
}

export interface FavoriteEvento {
  id: string;
  favoriteType: 'evento';
  name: string;
  slug: string;
  image: string;
  eventType: string;
  date: string;
  dateRelative: string;
  location: string;
  organizer: string;
  attendees: number;
  isLive: boolean;
  favoritedAt: string;
  favoritedAtRelative: string;
  score: number;
}

export interface FavoriteConcurso {
  id: string;
  favoriteType: 'concurso';
  name: string;
  slug: string;
  image: string;
  contestType: string;
  prize: string;
  endDate: string;
  endDateRelative: string;
  organizer: string;
  participants: number;
  status: 'activo' | 'finalizado' | 'por_iniciar';
  favoritedAt: string;
  favoritedAtRelative: string;
  score: number;
}

export interface FavoriteClub {
  id: string;
  favoriteType: 'club';
  name: string;
  slug: string;
  image: string;
  clubType: string;
  members: number;
  category: string;
  isActive: boolean;
  recentActivity: string;
  favoritedAt: string;
  favoritedAtRelative: string;
  score: number;
}

export type FavoriteItem = FavoriteRadio | FavoritePrograma | FavoriteLocutor | FavoriteEvento | FavoriteConcurso | FavoriteClub;

export interface FavoriteStats {
  totalFavorites: number;
  porRadio: number;
  porPrograma: number;
  porLocutor: number;
  porEvento: number;
  porConcurso: number;
  porClub: number;
  onAirNow: number;
  addedThisWeek: number;
  quickAccessItems: number;
}

export const favoriteTypeConfig: Record<FavoriteType, FavoriteTypeConfig> = {
  radio: {
    key: 'radio',
    label: 'Radio',
    labelPlural: 'Radios',
    icon: 'ri-radio-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    description: 'Emisoras que seguís para escuchar en vivo y recibir alertas cuando estén al aire.',
    routePrefix: '/radio',
  },
  programa: {
    key: 'programa',
    label: 'Programa',
    labelPlural: 'Programas',
    icon: 'ri-calendar-check-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    description: 'Programas que no te querés perder. Recibí recordatorios antes de que empiecen.',
    routePrefix: '/program',
  },
  locutor: {
    key: 'locutor',
    label: 'Locutor',
    labelPlural: 'Locutores',
    icon: 'ri-mic-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    description: 'Las voces que más te gustan. Sabé cuándo están al aire y en qué programas.',
    routePrefix: '/host',
  },
  evento: {
    key: 'evento',
    label: 'Evento',
    labelPlural: 'Eventos',
    icon: 'ri-calendar-event-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    description: 'Eventos que te interesan. No te pierdas ni uno con alertas y recordatorios.',
    routePrefix: '/eventos',
  },
  concurso: {
    key: 'concurso',
    label: 'Concurso',
    labelPlural: 'Concursos',
    icon: 'ri-trophy-line',
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    description: 'Concursos en los que querés participar. Seguí las fechas límite y los resultados.',
    routePrefix: '/competencias',
  },
  club: {
    key: 'club',
    label: 'Club',
    labelPlural: 'Clubes',
    icon: 'ri-group-line',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    description: 'Clubes de fans donde participás. Actividad, miembros y contenido exclusivo.',
    routePrefix: '/clubes',
  },
};

export const favoriteStats: FavoriteStats = {
  totalFavorites: 34,
  porRadio: 6,
  porPrograma: 7,
  porLocutor: 6,
  porEvento: 5,
  porConcurso: 5,
  porClub: 5,
  onAirNow: 7,
  addedThisWeek: 4,
  quickAccessItems: 8,
};

export const favoriteRadios: FavoriteRadio[] = [
  {
    id: 'fav-r-001',
    favoriteType: 'radio',
    name: 'La Mega 107.3 FM',
    slug: 'la-mega-107-3',
    image: 'https://readdy.ai/api/search-image?query=Professional%20radio%20station%20logo%20design%20bold%20typography%20warm%20golden%20energy%20waves%20abstract%20sound%20frequency%20pattern%20modern%20clean%20brand%20identity&width=200&height=200&seq=favs-radio-mega&orientation=squarish',
    genre: 'Música Urbana',
    location: 'Bogotá, Colombia',
    followers: 2840000,
    isLive: true,
    currentShow: 'El Despertador',
    favoritedAt: '2026-03-15T08:30:00Z',
    favoritedAtRelative: 'Marzo 2026',
    frequency: 'Diario',
    score: 98,
  },
  {
    id: 'fav-r-002',
    favoriteType: 'radio',
    name: 'Rock & Pop 95.1 FM',
    slug: 'rock-and-pop-95-1',
    image: 'https://readdy.ai/api/search-image?query=Bold%20rock%20radio%20station%20logo%20edgy%20typography%20electric%20guitar%20motif%20sound%20wave%20graphic%20dark%20background%20contrast%20modern%20brand%20identity&width=200&height=200&seq=favs-radio-rockpop&orientation=squarish',
    genre: 'Rock / Pop',
    location: 'Buenos Aires, Argentina',
    followers: 890000,
    isLive: true,
    currentShow: 'Noches de Rock',
    favoritedAt: '2026-04-02T14:15:00Z',
    favoritedAtRelative: 'Abril 2026',
    frequency: 'Frecuente',
    score: 92,
  },
  {
    id: 'fav-r-003',
    favoriteType: 'radio',
    name: 'Radio Éxito',
    slug: 'radio-exito',
    image: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20logo%20minimalist%20sound%20wave%20design%20warm%20coral%20and%20cream%20gradient%20clean%20geometric%20abstract%20composition%20studio%20branding%20identity&width=200&height=200&seq=favs-radio-exito&orientation=squarish',
    genre: 'Pop Latino',
    location: 'Ciudad de México, México',
    followers: 458000,
    isLive: true,
    currentShow: 'Éxitos de la Mañana',
    favoritedAt: '2026-05-10T09:00:00Z',
    favoritedAtRelative: 'Mayo 2026',
    frequency: 'Diario',
    score: 85,
  },
  {
    id: 'fav-r-004',
    favoriteType: 'radio',
    name: 'Tropicalísima 102.7 FM',
    slug: 'tropicalisima-102-7',
    image: 'https://readdy.ai/api/search-image?query=Vibrant%20tropical%20radio%20station%20logo%20warm%20sunset%20colors%20palm%20tree%20silhouette%20sound%20waves%20rhythmic%20pattern%20festive%20modern%20brand%20identity&width=200&height=200&seq=favs-radio-tropical&orientation=squarish',
    genre: 'Música Tropical',
    location: 'Cali, Colombia',
    followers: 750000,
    isLive: false,
    currentShow: null,
    favoritedAt: '2026-04-20T16:45:00Z',
    favoritedAtRelative: 'Abril 2026',
    frequency: 'Ocasional',
    score: 78,
  },
  {
    id: 'fav-r-005',
    favoriteType: 'radio',
    name: 'ElectroBeat FM',
    slug: 'electrobeat-fm',
    image: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20station%20logo%20minimalist%20neon%20violet%20and%20cyan%20design%20abstract%20sound%20wave%20grid%20broadcasting%20branding%20clean%20identity&width=200&height=200&seq=favs-radio-electro&orientation=squarish',
    genre: 'Electrónica',
    location: 'Barcelona, España',
    followers: 381000,
    isLive: true,
    currentShow: 'Deep Sessions',
    favoritedAt: '2026-06-01T20:00:00Z',
    favoritedAtRelative: 'Junio 2026',
    frequency: 'Frecuente',
    score: 82,
  },
  {
    id: 'fav-r-006',
    favoriteType: 'radio',
    name: 'Onda Cero 104.5 FM',
    slug: 'onda-cero-104-5',
    image: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20logo%20sleek%20typography%20cool%20blue%20tones%20sound%20waves%20abstract%20circular%20design%20contemporary%20brand%20identity%20professional&width=200&height=200&seq=favs-radio-ondacero&orientation=squarish',
    genre: 'Noticias / Deportes',
    location: 'Medellín, Colombia',
    followers: 2100000,
    isLive: true,
    currentShow: 'Carrusel Deportivo',
    favoritedAt: '2026-02-10T07:30:00Z',
    favoritedAtRelative: 'Febrero 2026',
    frequency: 'Diario',
    score: 95,
  },
];

export const favoritePrograms: FavoritePrograma[] = [
  {
    id: 'fav-p-001',
    favoriteType: 'programa',
    name: 'El Despertador',
    slug: 'el-despertador',
    image: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20logo%20warm%20sunrise%20colors%20coffee%20cup%20motif%20alarm%20clock%20graphic%20friendly%20typography%20energetic%20modern%20design&width=200&height=200&seq=favs-prog-despertador&orientation=squarish',
    station: 'La Mega 107.3 FM',
    host: ' Equipo El Despertador',
    schedule: 'L-V 6:00 - 10:00',
    genre: 'Morning Show',
    days: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
    onAir: true,
    nextEpisode: 'Mañana 6:00 AM',
    favoritedAt: '2026-03-15T08:35:00Z',
    favoritedAtRelative: 'Marzo 2026',
    score: 97,
  },
  {
    id: 'fav-p-002',
    favoriteType: 'programa',
    name: 'Noches de Rock',
    slug: 'noches-de-rock',
    image: 'https://readdy.ai/api/search-image?query=Rock%20music%20radio%20show%20logo%20electric%20guitar%20silhouette%20dark%20moody%20background%20neon%20accent%20typography%20vintage%20vinyl%20record%20aesthetic%20modern%20edgy%20design&width=200&height=200&seq=favs-prog-nochesrock&orientation=squarish',
    station: 'Rock & Pop 95.1 FM',
    host: 'Ricky Navarro',
    schedule: 'L-V 22:00 - 00:00',
    genre: 'Rock / Música',
    days: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
    onAir: true,
    nextEpisode: 'Hoy 10:00 PM',
    favoritedAt: '2026-04-02T14:20:00Z',
    favoritedAtRelative: 'Abril 2026',
    score: 91,
  },
  {
    id: 'fav-p-003',
    favoriteType: 'programa',
    name: 'Carrusel Deportivo',
    slug: 'carrusel-deportivo',
    image: 'https://readdy.ai/api/search-image?query=Sports%20radio%20show%20logo%20dynamic%20typography%20soccer%20ball%20graphic%20energy%20waves%20motion%20lines%20competitive%20spirit%20modern%20bold%20design&width=200&height=200&seq=favs-prog-carrusel&orientation=squarish',
    station: 'Onda Cero 104.5 FM',
    host: 'Diego Armando Paz',
    schedule: 'S-D 14:00 - 18:00',
    genre: 'Deportes',
    days: ['Sábado', 'Domingo'],
    onAir: false,
    nextEpisode: 'Sábado 2:00 PM',
    favoritedAt: '2026-02-10T07:35:00Z',
    favoritedAtRelative: 'Febrero 2026',
    score: 94,
  },
  {
    id: 'fav-p-004',
    favoriteType: 'programa',
    name: 'Éxitos de la Mañana',
    slug: 'exitos-de-la-manana',
    image: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20branding%20logo%20warm%20sunrise%20orange%20and%20yellow%20minimalist%20design%20clean%20geometric%20sun%20icon%20studio%20identity&width=200&height=200&seq=favs-prog-exitos&orientation=squarish',
    station: 'Radio Éxito',
    host: 'Carlos Mendoza y Ana Vega',
    schedule: 'L-V 6:00 - 10:00',
    genre: 'Entretenimiento',
    days: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
    onAir: true,
    nextEpisode: 'Mañana 6:00 AM',
    favoritedAt: '2026-05-10T09:05:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 84,
  },
  {
    id: 'fav-p-005',
    favoriteType: 'programa',
    name: 'Deep Sessions',
    slug: 'deep-sessions',
    image: 'https://readdy.ai/api/search-image?query=Deep%20house%20radio%20show%20logo%20minimalist%20violet%20gradient%20and%20dark%20indigo%20design%20clean%20abstract%20wave%20studio%20branding%20identity&width=200&height=200&seq=favs-prog-deep&orientation=squarish',
    station: 'ElectroBeat FM',
    host: 'DJ Nova',
    schedule: 'S-D 22:00 - 02:00',
    genre: 'Deep House',
    days: ['Sábado', 'Domingo'],
    onAir: true,
    nextEpisode: 'Sábado 10:00 PM',
    favoritedAt: '2026-06-01T20:05:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 81,
  },
  {
    id: 'fav-p-006',
    favoriteType: 'programa',
    name: 'Tardes con Valentina',
    slug: 'tardes-con-valentina',
    image: 'https://readdy.ai/api/search-image?query=Afternoon%20radio%20show%20logo%20warm%20feminine%20design%20sunset%20palette%20elegant%20typography%20heart%20motif%20soft%20curves%20modern%20inviting%20brand%20identity&width=200&height=200&seq=favs-prog-tardesval&orientation=squarish',
    station: 'Tropicalísima 102.7 FM',
    host: 'Valentina Orozco',
    schedule: 'L-V 15:00 - 18:00',
    genre: 'Variedades / Tropical',
    days: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
    onAir: false,
    nextEpisode: 'Lunes 3:00 PM',
    favoritedAt: '2026-04-20T16:50:00Z',
    favoritedAtRelative: 'Abril 2026',
    score: 77,
  },
  {
    id: 'fav-p-007',
    favoriteType: 'programa',
    name: 'El Mañanero Kalle',
    slug: 'el-mananero-kalle',
    image: 'https://readdy.ai/api/search-image?query=Energetic%20morning%20show%20logo%20urban%20style%20bold%20graffiti%20typography%20street%20culture%20sunrise%20gradient%20dynamic%20modern%20youthful%20brand%20identity&width=200&height=200&seq=favs-prog-mananero&orientation=squarish',
    station: 'La Kalle 96.3 FM',
    host: 'Equipo La Kalle',
    schedule: 'L-V 5:00 - 9:00',
    genre: 'Reggaetón / Humor',
    days: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'],
    onAir: false,
    nextEpisode: 'Mañana 5:00 AM',
    favoritedAt: '2026-05-28T11:00:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 79,
  },
];

export const favoriteLocutores: FavoriteLocutor[] = [
  {
    id: 'fav-l-001',
    favoriteType: 'locutor',
    name: 'Ricky Navarro',
    slug: 'ricky-navarro',
    photo: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20male%20radio%20host%20mid%20thirties%20warm%20friendly%20smile%20professional%20headshot%20studio%20portrait%20confident%20approachable%20personality&width=200&height=200&seq=favs-loc-ricky&orientation=squarish',
    role: 'Locutor Principal',
    station: 'Rock & Pop 95.1 FM',
    shows: ['Noches de Rock'],
    genre: 'Rock / Música',
    followers: 340000,
    onAir: true,
    favoritedAt: '2026-04-02T14:30:00Z',
    favoritedAtRelative: 'Abril 2026',
    score: 96,
  },
  {
    id: 'fav-l-002',
    favoriteType: 'locutor',
    name: 'Diego Armando Paz',
    slug: 'diego-armando-paz',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20Latino%20male%20sports%20commentator%20late%20thirties%20sharp%20focused%20expression%20headshot%20studio%20portrait%20clean%20modern%20look%20authoritative%20voice&width=200&height=200&seq=favs-loc-diegopaz&orientation=squarish',
    role: 'Narrador Deportivo',
    station: 'Onda Cero 104.5 FM',
    shows: ['Carrusel Deportivo'],
    genre: 'Deportes',
    followers: 210000,
    onAir: false,
    favoritedAt: '2026-02-10T07:40:00Z',
    favoritedAtRelative: 'Febrero 2026',
    score: 93,
  },
  {
    id: 'fav-l-003',
    favoriteType: 'locutor',
    name: 'Carlos Mendoza',
    slug: 'carlos-mendoza',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20radio%20host%20portrait%20hispanic%20male%2040s%20warm%20friendly%20smile%20studio%20headphones%20clean%20background%20soft%20lighting%20editorial%20photography&width=200&height=200&seq=favs-loc-carlos&orientation=squarish',
    role: 'Locutor Principal',
    station: 'Radio Éxito',
    shows: ['Éxitos de la Mañana'],
    genre: 'Entretenimiento',
    followers: 284000,
    onAir: true,
    favoritedAt: '2026-05-10T09:10:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 83,
  },
  {
    id: 'fav-l-004',
    favoriteType: 'locutor',
    name: 'Valentina Orozco',
    slug: 'valentina-orozco',
    photo: 'https://readdy.ai/api/search-image?query=Vibrant%20young%20Latina%20female%20radio%20host%20mid%20twenties%20bright%20energetic%20smile%20professional%20headshot%20tropical%20colorful%20style%20warm%20personality&width=200&height=200&seq=favs-loc-valentina&orientation=squarish',
    role: 'Conductora',
    station: 'Tropicalísima 102.7 FM',
    shows: ['Tardes con Valentina'],
    genre: 'Variedades / Tropical',
    followers: 185000,
    onAir: false,
    favoritedAt: '2026-04-20T16:55:00Z',
    favoritedAtRelative: 'Abril 2026',
    score: 76,
  },
  {
    id: 'fav-l-005',
    favoriteType: 'locutor',
    name: 'DJ Nova',
    slug: 'dj-nova',
    photo: 'https://readdy.ai/api/search-image?query=Professional%20electronic%20music%20DJ%20portrait%20female%20late%2020s%20stylish%20calm%20expression%20studio%20headphones%20clean%20dark%20background%20editorial%20photography&width=200&height=200&seq=favs-loc-nova&orientation=squarish',
    role: 'DJ Residente',
    station: 'ElectroBeat FM',
    shows: ['Deep Sessions'],
    genre: 'Electrónica',
    followers: 387000,
    onAir: true,
    favoritedAt: '2026-06-01T20:10:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 80,
  },
  {
    id: 'fav-l-006',
    favoriteType: 'locutor',
    name: 'La Flaca Hernández',
    slug: 'la-flaca-hernandez',
    photo: 'https://readdy.ai/api/search-image?query=Stylish%20young%20Latina%20female%20radio%20host%20mid%20twenties%20trendy%20urban%20fashion%20expressive%20face%20professional%20headshot%20bold%20personality%20studio%20portrait&width=200&height=200&seq=favs-loc-flaca&orientation=squarish',
    role: 'Co-Conductora',
    station: 'La Kalle 96.3 FM',
    shows: ['El Mañanero Kalle'],
    genre: 'Reggaetón / Urbano',
    followers: 320000,
    onAir: false,
    favoritedAt: '2026-05-28T11:10:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 78,
  },
];

export const favoriteEventos: FavoriteEvento[] = [
  {
    id: 'fav-e-001',
    favoriteType: 'evento',
    name: 'RFW Fest 2026',
    slug: 'rfw-fest-2026',
    image: 'https://readdy.ai/api/search-image?query=Massive%20music%20festival%20stage%20with%20colorful%20lighting%20crowd%20energy%20vibrant%20atmosphere%20outdoor%20concert%20celebration%20wide%20shot%20dynamic%20composition&width=400&height=250&seq=favs-evento-rfwfest&orientation=landscape',
    eventType: 'Festival',
    date: '2026-08-15T18:00:00Z',
    dateRelative: '15 de Agosto',
    location: 'Bogotá, Colombia',
    organizer: 'RFW',
    attendees: 25000,
    isLive: false,
    favoritedAt: '2026-06-10T12:00:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 99,
  },
  {
    id: 'fav-e-002',
    favoriteType: 'evento',
    name: 'Premios RFW 2026',
    slug: 'premios-rfw-2026',
    image: 'https://readdy.ai/api/search-image?query=Award%20show%20stage%20elegant%20lighting%20golden%20trophies%20red%20carpet%20atmosphere%20glamorous%20event%20production%20celebration%20sophisticated%20design&width=400&height=250&seq=favs-evento-premios&orientation=landscape',
    eventType: 'Premiación',
    date: '2026-11-20T19:00:00Z',
    dateRelative: '20 de Noviembre',
    location: 'Medellín, Colombia',
    organizer: 'RFW',
    attendees: 12000,
    isLive: false,
    favoritedAt: '2026-05-15T10:30:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 95,
  },
  {
    id: 'fav-e-003',
    favoriteType: 'evento',
    name: 'Noche de las Emisoras',
    slug: 'noche-de-las-emisoras',
    image: 'https://readdy.ai/api/search-image?query=Radio%20broadcasters%20gala%20event%20elegant%20venue%20warm%20ambient%20lighting%20people%20networking%20sophisticated%20party%20atmosphere%20professional%20gathering&width=400&height=250&seq=favs-evento-noche&orientation=landscape',
    eventType: 'Gala',
    date: '2026-07-08T20:00:00Z',
    dateRelative: '8 de Julio',
    location: 'Ciudad de México, México',
    organizer: 'RFW + Radio Éxito',
    attendees: 3500,
    isLive: false,
    favoritedAt: '2026-06-05T15:45:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 88,
  },
  {
    id: 'fav-e-004',
    favoriteType: 'evento',
    name: 'Copa RFW Final 2026',
    slug: 'copa-rfw-final-2026',
    image: 'https://readdy.ai/api/search-image?query=Championship%20final%20event%20stadium%20atmosphere%20dramatic%20lighting%20confetti%20celebration%20competitive%20sports%20energy%20crowd%20excitement%20dynamic%20composition&width=400&height=250&seq=favs-evento-copa&orientation=landscape',
    eventType: 'Campeonato',
    date: '2026-12-05T16:00:00Z',
    dateRelative: '5 de Diciembre',
    location: 'Buenos Aires, Argentina',
    organizer: 'RFW',
    attendees: 18000,
    isLive: false,
    favoritedAt: '2026-05-20T14:00:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 91,
  },
  {
    id: 'fav-e-005',
    favoriteType: 'evento',
    name: 'Encuentro de Locutores',
    slug: 'encuentro-de-locutores',
    image: 'https://readdy.ai/api/search-image?query=Professional%20conference%20event%20with%20speakers%20on%20stage%20audience%20engaged%20warm%20lighting%20modern%20venue%20networking%20atmosphere%20corporate%20gathering&width=400&height=250&seq=favs-evento-encuentro&orientation=landscape',
    eventType: 'Conferencia',
    date: '2026-09-12T09:00:00Z',
    dateRelative: '12 de Septiembre',
    location: 'Lima, Perú',
    organizer: 'RFW + Radio Felicidad',
    attendees: 1200,
    isLive: false,
    favoritedAt: '2026-06-12T08:00:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 72,
  },
];

export const favoriteConcursos: FavoriteConcurso[] = [
  {
    id: 'fav-c-001',
    favoriteType: 'concurso',
    name: 'Mejor Locutor del Año',
    slug: 'mejor-locutor-del-ano',
    image: 'https://readdy.ai/api/search-image?query=Golden%20microphone%20award%20on%20elegant%20podium%20dramatic%20lighting%20professional%20competition%20atmosphere%20prestige%20achievement%20recognition%20studio%20setting&width=400&height=250&seq=favs-conc-locutor&orientation=landscape',
    contestType: 'Votación',
    prize: '$5,000 USD + Trofeo RFW Oro',
    endDate: '2026-10-30T23:59:00Z',
    endDateRelative: '30 de Octubre',
    organizer: 'RFW',
    participants: 4800,
    status: 'activo',
    favoritedAt: '2026-06-08T16:00:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 94,
  },
  {
    id: 'fav-c-002',
    favoriteType: 'concurso',
    name: 'Batalla de Emisoras',
    slug: 'batalla-de-emisoras',
    image: 'https://readdy.ai/api/search-image?query=Epic%20radio%20station%20battle%20competition%20dramatic%20versus%20graphic%20sound%20wave%20clash%20vibrant%20energy%20dynamic%20composition%20professional%20broadcast%20showdown&width=400&height=250&seq=favs-conc-batalla&orientation=landscape',
    contestType: 'Competencia',
    prize: 'Estudio Profesional + $10,000 USD',
    endDate: '2026-09-15T23:59:00Z',
    endDateRelative: '15 de Septiembre',
    organizer: 'RFW',
    participants: 128,
    status: 'activo',
    favoritedAt: '2026-05-30T09:30:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 90,
  },
  {
    id: 'fav-c-003',
    favoriteType: 'concurso',
    name: 'Trivia RFW Semanal',
    slug: 'trivia-rfw-semanal',
    image: 'https://readdy.ai/api/search-image?query=Exciting%20trivia%20game%20show%20set%20colorful%20lighting%20question%20marks%20floating%20buzzers%20ready%20playful%20competitive%20atmosphere%20studio%20production%20design&width=400&height=250&seq=favs-conc-trivia&orientation=landscape',
    contestType: 'Trivia',
    prize: 'Insignia Oro + 1,000 Puntos RFW',
    endDate: '2026-07-01T23:59:00Z',
    endDateRelative: '1 de Julio',
    organizer: 'RFW',
    participants: 12000,
    status: 'activo',
    favoritedAt: '2026-06-14T10:00:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 85,
  },
  {
    id: 'fav-c-004',
    favoriteType: 'concurso',
    name: 'Crea tu Programa',
    slug: 'crea-tu-programa',
    image: 'https://readdy.ai/api/search-image?query=Creative%20radio%20production%20contest%20concept%20microphone%20surrounded%20by%20idea%20lightbulbs%20colorful%20creative%20energy%20inspiration%20atmosphere%20studio%20background%20professional&width=400&height=250&seq=favs-conc-crea&orientation=landscape',
    contestType: 'Creación',
    prize: 'Programa al aire por 3 meses + Mentoría',
    endDate: '2026-08-20T23:59:00Z',
    endDateRelative: '20 de Agosto',
    organizer: 'RFW + La Mega',
    participants: 2400,
    status: 'activo',
    favoritedAt: '2026-06-03T13:15:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 87,
  },
  {
    id: 'fav-c-005',
    favoriteType: 'concurso',
    name: 'DJ Challenge 2026',
    slug: 'dj-challenge-2026',
    image: 'https://readdy.ai/api/search-image?query=DJ%20competition%20event%20turntables%20neon%20lighting%20energetic%20crowd%20club%20atmosphere%20professional%20mixing%20battle%20dynamic%20stage%20production&width=400&height=250&seq=favs-conc-dj&orientation=landscape',
    contestType: 'Competencia',
    prize: '$3,000 USD + Set en RFW Fest',
    endDate: '2026-07-25T23:59:00Z',
    endDateRelative: '25 de Julio',
    organizer: 'ElectroBeat FM',
    participants: 560,
    status: 'activo',
    favoritedAt: '2026-06-01T20:30:00Z',
    favoritedAtRelative: 'Junio 2026',
    score: 82,
  },
];

export const favoriteClubs: FavoriteClub[] = [
  {
    id: 'fav-cl-001',
    favoriteType: 'club',
    name: 'Club Fans La Mega',
    slug: 'club-fans-la-mega',
    image: 'https://readdy.ai/api/search-image?query=Exclusive%20fan%20club%20community%20logo%20warm%20golden%20and%20coral%20design%20crown%20emblem%20premium%20membership%20elite%20group%20modern%20brand%20identity&width=200&height=200&seq=favs-club-mega&orientation=squarish',
    clubType: 'Premium',
    members: 28500,
    category: 'Radio',
    isActive: true,
    recentActivity: 'Nuevo contenido exclusivo: entrevista con Ricky Navarro tras bastidores',
    favoritedAt: '2026-03-15T09:00:00Z',
    favoritedAtRelative: 'Marzo 2026',
    score: 96,
  },
  {
    id: 'fav-cl-002',
    favoriteType: 'club',
    name: 'Legión del Rock',
    slug: 'legion-del-rock',
    image: 'https://readdy.ai/api/search-image?query=Rock%20music%20fan%20club%20logo%20dark%20edgy%20design%20electric%20guitar%20skull%20motif%20bold%20typography%20metal%20community%20rebellious%20spirit%20modern%20brand&width=200&height=200&seq=favs-club-legion&orientation=squarish',
    clubType: 'Oficial',
    members: 15600,
    category: 'Género Musical',
    isActive: true,
    recentActivity: 'Encuesta: Mejor álbum de rock latino 2026 — ¡votá ya!',
    favoritedAt: '2026-04-02T14:45:00Z',
    favoritedAtRelative: 'Abril 2026',
    score: 89,
  },
  {
    id: 'fav-cl-003',
    favoriteType: 'club',
    name: 'Club de Oyentes Top',
    slug: 'club-de-oyentes-top',
    image: 'https://readdy.ai/api/search-image?query=Elite%20listener%20community%20logo%20sophisticated%20design%20gold%20and%20navy%20tones%20star%20emblem%20exclusive%20membership%20premium%20status%20modern%20brand%20identity&width=200&height=200&seq=favs-club-oyentes&orientation=squarish',
    clubType: 'VIP',
    members: 8900,
    category: 'Oyentes',
    isActive: true,
    recentActivity: 'Acceso anticipado a Preventa RFW Fest 2026 — ¡solo VIP!',
    favoritedAt: '2026-05-22T17:30:00Z',
    favoritedAtRelative: 'Mayo 2026',
    score: 92,
  },
  {
    id: 'fav-cl-004',
    favoriteType: 'club',
    name: 'Comunidad Deportiva RFW',
    slug: 'comunidad-deportiva-rfw',
    image: 'https://readdy.ai/api/search-image?query=Sports%20community%20fan%20club%20logo%20dynamic%20design%20soccer%20ball%20shield%20emblem%20bold%20typography%20passionate%20energy%20modern%20brand%20identity&width=200&height=200&seq=favs-club-deportiva&orientation=squarish',
    clubType: 'Oficial',
    members: 22400,
    category: 'Deportes',
    isActive: true,
    recentActivity: 'Debate en vivo: ¿Quién gana la Copa RFW 2026?',
    favoritedAt: '2026-02-10T08:00:00Z',
    favoritedAtRelative: 'Febrero 2026',
    score: 86,
  },
  {
    id: 'fav-cl-005',
    favoriteType: 'club',
    name: 'Tropical Lovers',
    slug: 'tropical-lovers',
    image: 'https://readdy.ai/api/search-image?query=Tropical%20music%20fan%20community%20logo%20vibrant%20warm%20sunset%20palette%20palm%20tree%20rhythm%20icon%20festive%20spirit%20joyful%20modern%20brand%20identity&width=200&height=200&seq=favs-club-tropical&orientation=squarish',
    clubType: 'Oficial',
    members: 12300,
    category: 'Género Musical',
    isActive: true,
    recentActivity: 'Playlist colaborativa: Las Mejores Salsas de Todos los Tiempos',
    favoritedAt: '2026-04-20T17:00:00Z',
    favoritedAtRelative: 'Abril 2026',
    score: 74,
  },
];

export const allFavorites: FavoriteItem[] = [
  ...favoriteRadios,
  ...favoritePrograms,
  ...favoriteLocutores,
  ...favoriteEventos,
  ...favoriteConcursos,
  ...favoriteClubs,
];

export const quickAccessFavorites: FavoriteItem[] = allFavorites
  .sort((a, b) => b.score - a.score)
  .slice(0, 8);

export const favoriteRules = [
  {
    id: 1,
    title: 'Máximo 1 clic para acceder',
    description: 'Todo favorito está a un solo toque de distancia. Desde cualquier parte de RFW, tus favoritos son accesibles instantáneamente. No hay menús anidados, no hay pasos extra. Un clic y estás donde querés estar.',
    icon: 'ri-cursor-line',
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
  },
  {
    id: 2,
    title: 'Todo en un solo lugar',
    description: 'Radios, programas, locutores, eventos, concursos y clubes. Seis tipos de contenido favorito organizados en un solo hub. Sin tener que navegar por toda la plataforma para encontrar lo que te gusta.',
    icon: 'ri-stack-line',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
  },
  {
    id: 3,
    title: 'Siempre actualizado',
    description: 'Tus favoritos reflejan en tiempo real lo que está pasando: quién está al aire, qué evento se acerca, qué concurso está por cerrar. La información que importa, cuando la necesitás.',
    icon: 'ri-refresh-line',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
];

export function getFavoritesByType(type: FavoriteType | 'todas'): FavoriteItem[] {
  if (type === 'todas') return allFavorites;
  return allFavorites.filter((f) => f.favoriteType === type);
}

export function getOnAirFavorites(): FavoriteItem[] {
  return allFavorites.filter((f) => {
    if (f.favoriteType === 'radio') return (f as FavoriteRadio).isLive;
    if (f.favoriteType === 'programa') return (f as FavoritePrograma).onAir;
    if (f.favoriteType === 'locutor') return (f as FavoriteLocutor).onAir;
    return false;
  });
}

export function getQuickAccess(): FavoriteItem[] {
  return quickAccessFavorites;
}